import { Injectable } from '@angular/core';
import {
    menuLinkNames,
    ApplicationConstants,
    CUSTOM_CLAIM_ROLES, COGNITO_GROUPS_CLAIM
} from '@app/constants/application-constants';
import { environment } from '@env/environment';
import { AppState, getLoggedIn } from '@app/store/app.reducers';
import { Store, select } from '@ngrx/store';
import { combineLatest, concat, forkJoin, Observable, of, zip } from 'rxjs';
import { map, mergeMap, shareReplay, withLatestFrom } from 'rxjs/operators';
import { AuthService } from '@app/services/auth.service';
import { ContactService } from './contact.service';
import { OrganisationConfig } from '@app/model/organisationConfig';

interface MenuLink {
    name: string;
    url?: string;
    external: boolean;
    children?: MenuLink[];
    id: string;

    command?(): any;
}

@Injectable()
export class MenuLinksService {

    menuLinks$: Observable<MenuLink[]>;

    constructor(private store: Store<AppState>, private contactService: ContactService) {
        this.menuLinks$ = this.getMenuLinks$();
    }

    private getMenuLinks$() {
        return this.store.pipe(
            select(getLoggedIn),
            mergeMap(isLoggedIn => combineLatest(
                [of(this.getRoleNamesFromAccessToken(isLoggedIn)),
                    this.getOrganisationConfig$(isLoggedIn)]
            )),
            map(([groupNames, orgConfig]) => this.getVisibleMenuLinks(groupNames, orgConfig)),
            shareReplay(1)
        );
    }

    private getRoleNamesFromAccessToken(loggedIn: boolean): string[] {
        if (!loggedIn) {
            return [];
        }

        // get the token from local storage
        const accessToken = AuthService.getAccessToken();
        if (!accessToken) {
            return [];
        }

        // decode it
        const accessTokenPayload = JSON.parse(atob(accessToken.split('.')[1]));

        if (accessTokenPayload[CUSTOM_CLAIM_ROLES] != null) {
            return accessTokenPayload[CUSTOM_CLAIM_ROLES];
        }

        return accessTokenPayload[COGNITO_GROUPS_CLAIM] || [];
    }

    private getOrganisationConfig$(loggedin: boolean): Observable<OrganisationConfig> {
        if (!loggedin) {
            return of(new OrganisationConfig());
        } else {
            return this.contactService.getOrganisationConfig(AuthService.getAccessToken());
        }
    }

    private getVisibleMenuLinks(groupNames: string[], organisationConfig: OrganisationConfig) {
        const menuLinks = ApplicationConstants.menuLinks
            .filter(link => {
                if (link.name === menuLinkNames.HONEY_BLENDING) {
                    return groupNames.includes(environment.honeyBlendingGroupName) ||
                        groupNames.includes(environment.honeyBlendingGroupName.replace(' ', ''));
                }
                if (link.name === menuLinkNames.HONEY_FORECASTING) {
                    return groupNames.includes(environment.honeyForecastingGroupName) ||
                        groupNames.includes(environment.honeyForecastingGroupName.replace(' ', ''));
                }

                if (link.name === menuLinkNames.SAMPLING_SUPPLIES) {
                    return organisationConfig.canOrderSamplingSupplies;
                }

                if (link.name === menuLinkNames.LAB_TESTS) {
                    return organisationConfig.canOrderLabTests;
                }

                return !environment.hiddenLinks.includes(link.name);
            })
            .map(link => {
                // substitute the environment-specific URL
                if (link.name === menuLinkNames.HONEY_BLENDING) {
                    return { ...link, url: environment.honeyBlendingUrl };
                }
                if (link.name === menuLinkNames.HONEY_FORECASTING) {
                    return { ...link, url: environment.honeyForecastingUrl };
                }

                return { ...link, };
            });

        return menuLinks;
    }

}
